import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Scroll, ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { api, formatDate } from "@/lib/gado";
import { Button } from "@/components/ui/button";

const PAGE_SIZE = 30;

const ACTION_LABELS = {
  "admin.users.view": "Visualizou usuário",
  "admin.users.plan": "Alterou plano",
  "admin.users.block": "Bloqueou usuário",
  "admin.users.unblock": "Desbloqueou usuário",
  "admin.users.delete": "Excluiu conta (LGPD)",
  "admin.users.role": "Alterou papel (admin)",
};

export default function AdminAudit() {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-audit", page],
    queryFn: async () => (await api.get("/admin/audit", { params: { page, page_size: PAGE_SIZE } })).data,
  });

  const items = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const fmtTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return `${formatDate(iso)} ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
  };

  return (
    <div className="space-y-5" data-testid="admin-audit-page">
      <div>
        <h1 className="font-cabinet text-2xl md:text-3xl font-bold text-gado-ink tracking-tight">Auditoria</h1>
        <p className="text-gado-muted text-sm">Registro imutável das ações administrativas.</p>
      </div>

      <div className="space-y-2" data-testid="audit-list">
        {isLoading && <p className="text-sm text-gado-muted text-center py-8">Carregando…</p>}
        {!isLoading && items.length === 0 && (
          <div className="rounded-xl border border-gado-line bg-white text-center py-12">
            <Scroll size={32} className="mx-auto text-gado-line mb-2" />
            <p className="text-sm text-gado-muted">Nenhuma ação registrada ainda.</p>
          </div>
        )}
        {items.map((log, i) => (
          <div
            key={`${log.created_at}-${i}`}
            className="rounded-xl border border-gado-line bg-white px-3.5 py-3"
            data-testid={`audit-item-${i}`}
          >
            <div className="flex items-center justify-between gap-2 min-w-0">
              <p className="text-sm font-semibold text-gado-ink truncate">
                {ACTION_LABELS[log.action] || log.action}
              </p>
              <span className={`text-[10px] uppercase tracking-wider font-bold shrink-0 ${log.outcome === "success" ? "text-gado-green" : "text-destructive"}`}>
                {log.outcome === "success" ? "OK" : "Falha"}
              </span>
            </div>
            <p className="text-xs text-gado-muted mt-0.5 truncate">
              {log.actor_email}
              {log.metadata?.email && ` → ${log.metadata.email}`}
              {log.metadata?.from && log.metadata?.to && ` · ${log.metadata.from} → ${log.metadata.to}`}
              {` · ${fmtTime(log.created_at)}`}
            </p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-gado-muted">{total} registro(s) · página {page} de {totalPages}</p>
        <div className="flex gap-1.5">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="audit-prev">
            <ArrowLeft size={14} />
          </Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="audit-next">
            <ArrowRight size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
}
